/**
 * QR Code Display Component
 *
 * Shows WhatsApp connection status, QR code for linking,
 * and allows admins to disconnect and scan a new QR code.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { useTranslations } from '../../context/TranslationsContext';
import { whatsappAPI } from '../../api';

const formatPhone = (phone) => {
  if (!phone) return '';
  return phone.split(':')[0].split('@')[0];
};

const QRCodeDisplay = () => {
  const { state } = useApp();
  const { translations } = useTranslations();
  const t = translations[state.currentLanguage];

  const [isConnected, setIsConnected] = useState(false);
  const [connectedUser, setConnectedUser] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const pollForQRCode = useCallback(async () => {
    for (let attempt = 0; attempt < 10; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      try {
        const qrData = await whatsappAPI.getQRCode();

        if (qrData.qr) {
          setQrCodeUrl(whatsappAPI.getQRCodeImageUrl());
          setLastUpdated(new Date());
          setError(null);
          return true;
        }
      } catch (err) {
        console.error('Error polling for QR code:', err);
      }
    }

    return false;
  }, []);

  const loadConnectionState = useCallback(async ({ showLoader = false } = {}) => {
    if (showLoader) {
      setLoading(true);
    }
    setError(null);

    try {
      let connected = false;
      let user = null;

      try {
        const status = await whatsappAPI.getConnectionStatus();
        connected = Boolean(status?.whatsapp?.connected);
        user = status?.whatsapp?.user || null;
      } catch (statusErr) {
        console.warn('Could not load WhatsApp status, falling back to QR check:', statusErr);
      }

      if (connected) {
        setIsConnected(true);
        setConnectedUser(user);
        setQrCodeUrl(null);
        setError(null);
        setLastUpdated(new Date());
        return;
      }

      setIsConnected(false);
      setConnectedUser(null);

      const qrData = await whatsappAPI.getQRCode();

      if (qrData.qr) {
        setQrCodeUrl(whatsappAPI.getQRCodeImageUrl());
        setLastUpdated(new Date());
        setError(null);
      } else {
        setQrCodeUrl(null);
        setError(t.noQRCodeAvailableClickUpdate);
      }
    } catch (err) {
      console.error('Error loading WhatsApp connection state:', err);
      setIsConnected(false);
      setConnectedUser(null);
      setQrCodeUrl(null);
      setError(t.failedToLoadQRCode);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, [t.failedToLoadQRCode, t.noQRCodeAvailableClickUpdate]);

  useEffect(() => {
    loadConnectionState({ showLoader: true });
    const interval = setInterval(() => loadConnectionState(), 15000);
    return () => clearInterval(interval);
  }, [loadConnectionState]);

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);

    try {
      await whatsappAPI.refreshQRCode();
      const hasQR = await pollForQRCode();

      if (!hasQR) {
        setQrCodeUrl(null);
        setError(t.noQRCodeAvailableAfterRefresh);
      }
    } catch (err) {
      console.error('Error refreshing QR code:', err);
      setError(t.failedToUpdateQRCode);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);

    try {
      await whatsappAPI.disconnectWhatsApp();
      setIsConnected(false);
      setConnectedUser(null);
      setQrCodeUrl(null);

      const hasQR = await pollForQRCode();

      if (!hasQR) {
        setError(t.noQRCodeAvailableAfterRefresh);
      }
    } catch (err) {
      console.error('Error disconnecting WhatsApp:', err);
      setError(t.disconnectFailed);
    } finally {
      setDisconnecting(false);
      setLoading(false);
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US');
  };

  const phone = formatPhone(connectedUser?.phone || connectedUser?.id);
  const deviceName = connectedUser?.name || t.notSpecified;

  return (
    <div className="bg-white shadow-lg rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t.whatsappQRCode}</h3>
          <p className="text-sm text-gray-600">
            {isConnected ? t.whatsappConnectedMessage : t.scanCodeToConnect}
          </p>
        </div>

        {!isConnected && (
          <button
            onClick={handleRefresh}
            disabled={loading || disconnecting}
            className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {loading ? t.updating : t.update}
          </button>
        )}
      </div>

      <div className="flex flex-col items-center space-y-4">
        {loading || disconnecting ? (
          <div className="flex flex-col items-center space-y-3 py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
            <p className="text-gray-600">
              {disconnecting ? t.disconnecting : t.loadingQRCode}
            </p>
          </div>
        ) : isConnected ? (
          <div className="w-full max-w-md">
            <div className="flex flex-col items-center space-y-4 py-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-3xl">✅</span>
              </div>

              <div className="text-center">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 mb-3">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2 rtl:mr-0 rtl:ml-2"></span>
                  {t.connected}
                </div>
                <h4 className="text-lg font-semibold text-gray-900">{t.whatsappDeviceConnected}</h4>
              </div>

              <div className="w-full bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">{t.connectedDevice}:</span>
                  <span className="font-medium text-gray-900">{deviceName}</span>
                </div>
                {phone && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{t.phoneNumber}:</span>
                    <span className="font-medium text-gray-900" dir="ltr">{phone}</span>
                  </div>
                )}
                {lastUpdated && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{t.lastUpdate}:</span>
                    <span className="font-medium text-gray-900">{formatTime(lastUpdated)}</span>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-500 text-center">{t.disconnectToScanNewQR}</p>

              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {t.disconnectWhatsApp}
              </button>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center space-y-3 py-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">❌</span>
            </div>
            <p className="text-red-600 text-center">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors duration-200"
            >
              {t.tryAgain}
            </button>
          </div>
        ) : qrCodeUrl ? (
          <div className="flex flex-col items-center space-y-4">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
              <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2 rtl:mr-0 rtl:ml-2"></span>
              {t.disconnected}
            </div>

            <div className="bg-white p-4 rounded-lg shadow-lg border-2 border-gray-200">
              <img
                key={qrCodeUrl}
                src={qrCodeUrl}
                alt="WhatsApp QR Code"
                className="w-64 h-64 object-contain"
                onError={() => setError(t.failedToDisplayQRCode)}
              />
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">{t.scanThisCodeWithWhatsapp}</p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2">
                <p className="text-xs text-yellow-800">⚠️ {t.ifPhoneCannotReadCode}</p>
              </div>
              {lastUpdated && (
                <p className="text-xs text-gray-500">
                  {t.lastUpdate}: {formatTime(lastUpdated)}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-3 py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">📱</span>
            </div>
            <p className="text-gray-600 text-center">{t.noQRCodeAvailable}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              {t.update}
            </button>
          </div>
        )}
      </div>

      {!isConnected && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-start space-x-3 rtl:space-x-reverse">
            <div className="flex-shrink-0">
              <span className="text-blue-600 text-lg">💡</span>
            </div>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">{t.howToConnect}:</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>{t.openWhatsappOnPhone}</li>
                <li>{t.clickThreeDotsOrSettings}</li>
                <li>{t.chooseLinkedDevices}</li>
                <li>{t.clickLinkDevice}</li>
                <li>{t.scanQRCodeAbove}</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRCodeDisplay;
